import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import { CountdownDisplayProvider } from '@/hooks/countdownDisplay';
import { useCountdown } from '@/hooks/useCountdown';
import { usePolling } from '@/hooks/usePolling';
import { refreshStatus, useSessionDispatch, useSessionState } from '@/state';
import { selectViewState } from '@/state/selectors';

const POLLABLE: ReadonlySet<string> = new Set([
  'processing',
  'underReview',
  'walletInstructions',
  'submitProof',
]);

/**
 * The one countdown and the one poller. Mounted once in `CashierLayout` (I-9). Provides remaining
 * time to screens without putting timer ticks in session state.
 */
export function SessionTimers({ children }: { children: ReactNode }) {
  const { t } = useTranslation('cashier');
  const state = useSessionState();
  const dispatch = useSessionDispatch();
  const viewState = selectViewState(state);
  const token = state.token;
  const expiresAt =
    viewState === 'underReview'
      ? (state.deposit?.verificationExpiresAt ?? null)
      : (state.deposit?.expiresAt ?? null);

  const milestoneMessages = useMemo(
    () => ({
      300_000: t('countdown.milestone5m'),
      120_000: t('countdown.milestone2m'),
      60_000: t('countdown.milestone1m'),
      30_000: t('countdown.milestone30s'),
    }),
    [t],
  );
  const urgentMessage = t('countdown.urgentAnnounce');

  const countdown = useCountdown({
    expiresAt,
    warnMs: env.sessionWarnSeconds * 1000,
    onExpired: () => {
      if (token === null) return;
      const controller = new AbortController();
      void refreshStatus(dispatch, token, controller.signal).catch(() => undefined);
    },
    milestoneMessages,
    urgentMessage,
  });

  usePolling({
    enabled: token !== null && POLLABLE.has(viewState) && state.error?.status !== 401,
    resetKey: state.deposit?.status ?? '',
    onTick: (signal) => {
      if (token === null) return Promise.resolve();
      return refreshStatus(dispatch, token, signal);
    },
  });

  return <CountdownDisplayProvider value={countdown}>{children}</CountdownDisplayProvider>;
}
